import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  label?: string;
}
interface State {
  error: Error | null;
}

/** Keeps a single component crash (e.g. a modal) from blanking the whole app. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label, error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4">
          <div className="max-w-md rounded-2xl border border-rose-500/40 bg-zinc-900 p-5 text-sm text-rose-200">
            <p className="font-semibold">Something went wrong{this.props.label ? ` in ${this.props.label}` : ''}.</p>
            <p className="mt-2 break-words text-xs text-rose-300/80">{this.state.error.message}</p>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="mt-4 rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-black"
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
